import { defineStore } from 'pinia'
import type { RealtimeChannel } from '@supabase/supabase-js'
import type { SpecialtyModule, ClinicModule } from '~/types'

interface ClinicModulesState {
  catalog:        SpecialtyModule[]
  activeModules:  string[]
  // Full clinic_modules rows for the current clinic (incl. `config`) — kept
  // alongside `activeModules` (which only carries the keys) because
  // per-module config (e.g. spaces.pediatric_overlap_warning) lives on THIS
  // row, never on the `catalog` (specialty_modules) entry from moduleByKey.
  clinicModuleRows: ClinicModule[]
  loading:        boolean
  saving:         boolean
  error:          string | null
  channel:        RealtimeChannel | null
  currentClinicId: string | null
}

export const useClinicModulesStore = defineStore('clinic_modules', {
  state: (): ClinicModulesState => ({
    catalog:         [],
    activeModules:   [],
    clinicModuleRows: [],
    loading:         false,
    saving:          false,
    error:           null,
    channel:         null,
    currentClinicId: null,
  }),

  getters: {
    hasModule: (state) => (key: string): boolean => {
      return state.activeModules.includes(key)
    },

    moduleByKey: (state) => (key: string): SpecialtyModule | null => {
      return state.catalog.find(m => m.key === key) ?? null
    },

    // Config lives on the CLINIC's clinic_modules row, NOT on the catalog
    // (specialty_modules) entry — moduleByKey(key)?.config would always be
    // undefined. Returns {} when the module has no row yet (disabled/unloaded).
    moduleConfig: (state) => (key: string): Record<string, unknown> => {
      return (state.clinicModuleRows.find(m => m.module_key === key)?.config ?? {}) as Record<string, unknown>
    },

    guardedRouteToModule: (state): Record<string, string> => {
      const map: Record<string, string> = {}
      for (const mod of state.catalog) {
        for (const route of mod.guarded_routes) {
          map[route] = mod.key
        }
      }
      return map
    },

    /**
     * Gate comercial por PermissionModule (spec kernel §3.1).
     * Sin dueño en catálogo → permitido (legacy). Con dueños → permitido si
     * ALGÚN módulo comercial que lo reclama está efectivamente activo.
     */
    permissionModuleAllowed(state): (pm: string) => boolean {
      return (pm: string): boolean => {
        const owners = state.catalog.filter(m => (m.permission_modules ?? []).includes(pm))
        if (owners.length === 0) return true
        return owners.some(m => state.activeModules.includes(m.key))
      }
    },
  },

  actions: {
    async loadCatalog() {
      const supabase = useSupabaseClient()
      this.loading = true
      this.error   = null
      try {
        const { data, error } = await supabase
          .from('specialty_modules')
          .select('*')
          .eq('is_active', true)
        if (error) throw error
        this.catalog = (data as SpecialtyModule[]) ?? []
      } catch (e: any) {
        console.error(e)
        this.error = 'Error al cargar el catálogo de módulos'
      } finally {
        this.loading = false
      }
    },

    async loadForCurrentClinic(clinicId: string) {
      const supabase = useSupabaseClient()
      this.loading = true
      this.error   = null
      try {
        // Cargar catálogo + lista EFECTIVA (core/grandfather/vendible — semántica
        // en SQL, única fuente de verdad; spec kernel §2.2) + filas de la clínica
        // y NO asignar ninguno a state hasta que los TRES resuelvan. Si sólo el catálogo llegara a
        // asignarse y el RPC de abajo fallara (blip de red/race de auth), el
        // catch dejaría catalog poblado pero activeModules en [] — eso rompe el
        // fail-open de middleware/03.module.global.ts (`if (!modules.catalog
        // .length) return`), porque catalog.length ya no sería 0: una falla
        // transitoria del RPC apagaría TODOS los módulos vendible de golpe en
        // vez de fallar abierto como pretende el middleware.
        const { data: catalogData, error: catalogError } = await supabase
          .from('specialty_modules')
          .select('*')
          .eq('is_active', true)
        if (catalogError) throw catalogError

        const { data: effective, error: effError } = await supabase
          .rpc('get_effective_modules', { p_clinic_id: clinicId })
        if (effError) throw effError

        // Filas completas de clinic_modules (incl. `config`). El RPC de arriba
        // resuelve QUÉ módulos están activos; la configuración por clínica
        // (p. ej. spaces.pediatric_overlap_warning) vive en ESTA fila y en
        // ninguna otra parte. Se conserva el filtro `enabled=true` de dev: un
        // módulo apagado no debe exponer su config, porque quien la consume
        // (usePediatricOverlap) sólo mira la bandera, no `hasModule`. Un módulo
        // efectivo por core/grandfather sin fila queda con config {} = default.
        const { data: modulesData, error: modulesError } = await supabase
          .from('clinic_modules')
          .select('*')
          .eq('clinic_id', clinicId)
          .eq('enabled', true)
        if (modulesError) throw modulesError

        this.catalog = (catalogData as SpecialtyModule[]) ?? []
        this.activeModules = ((effective as { module_key: string; enabled: boolean }[]) ?? [])
          .filter(r => r.enabled)
          .map(r => r.module_key)
        this.clinicModuleRows = (modulesData as ClinicModule[]) ?? []
        this.currentClinicId  = clinicId
      } catch (e: any) {
        console.error(e)
        this.error = 'Error al cargar los módulos de la clínica'
      } finally {
        this.loading = false
      }
    },

    subscribe(clinicId: string) {
      const supabase = useSupabaseClient()
      if (!clinicId || this.channel) return

      this.channel = supabase
        .channel(`clinic_modules:${clinicId}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'clinic_modules', filter: `clinic_id=eq.${clinicId}` },
          () => { this.loadForCurrentClinic(clinicId) },
        )
        .subscribe((status: string) => {
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            setTimeout(() => { this.unsubscribe(); this.subscribe(clinicId) }, 5000)
          }
        })
    },

    unsubscribe() {
      if (!this.channel) return
      const supabase = useSupabaseClient()
      try { supabase.removeChannel(this.channel) } catch { /* noop */ }
      this.channel = null
    },
  },
})
